/**
 * useAircraftCardSync - Bridges aircraftEditorStore ↔ aircraftCardStore
 *
 * Subscribes to selection and drag state from the editor store and
 * drives the card display state machine accordingly:
 *
 * - selectedAllocationId changes → openCompact() or closeCard()
 * - isDragging true  → enterMoveBar()
 * - isDragging false → exitMoveBar() (if was in moveBar)
 */

import { useEffect, useRef } from 'react'
import { useAircraftEditorStore } from '@/stores/aircraftEditorStore'
import { useAircraftCardStore } from '@/stores/aircraftCardStore'

export function useAircraftCardSync() {
  const prevSelectionRef = useRef<string | null>(null)
  const prevDraggingRef = useRef(false)

  const selectedAllocationId = useAircraftEditorStore(
    (s) => s.selectedAllocationId
  )
  const isDragging = useAircraftEditorStore((s) => s.isDragging)

  const openCompact = useAircraftCardStore((s) => s.openCompact)
  const closeCard = useAircraftCardStore((s) => s.closeCard)
  const enterMoveBar = useAircraftCardStore((s) => s.enterMoveBar)
  const exitMoveBar = useAircraftCardStore((s) => s.exitMoveBar)
  const cardMode = useAircraftCardStore((s) => s.cardState.mode)

  // Selection changes → open compact or close
  useEffect(() => {
    if (selectedAllocationId !== prevSelectionRef.current) {
      prevSelectionRef.current = selectedAllocationId

      if (selectedAllocationId) {
        openCompact()
      } else {
        closeCard()
      }
    }
  }, [selectedAllocationId, openCompact, closeCard])

  // Drag state changes → enter/exit moveBar
  useEffect(() => {
    if (isDragging && !prevDraggingRef.current) {
      enterMoveBar()
    } else if (!isDragging && prevDraggingRef.current) {
      if (cardMode === 'moveBar') {
        exitMoveBar()
      }
    }
    prevDraggingRef.current = isDragging
  }, [isDragging, cardMode, enterMoveBar, exitMoveBar])
}
